import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import Tabs from "@/components/Usados/Tabs";
import ResponsiveTable, { Column } from "@/components/Usados/ResponsiveTable";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  Users,
  ListTodo,
  DollarSign,
} from "lucide-react";
import { apiFetch } from "@/service/api";

interface Proyecto {
  id: number;
  nombre: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_entrega_estimada: string;
  presupuesto: number;
  tipo_proyecto_id: number;
  estado_proyecto_id: number;
}
interface Actividad {
  id: number;
  nombre: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_fin_estimada: string;
  estado_actividad: number;
  prioridad: number;
}
interface EmpleadoProyecto {
  id: number;
  equipo_proyecto: { nombre: string };
  empleado: { nombres: string; apellidos: string; correo_electronico: string };
  rol_proyecto: { nombre: string };
  tarea: string;
}

export default function VerProyecto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proyecto, setProyecto] = useState<Proyecto | null>(null);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [equipo, setEquipo] = useState<EmpleadoProyecto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [resProyecto, resActividades, resEquipo] = await Promise.all([
          apiFetch(`proyectos/find-one/${id}`),
          apiFetch(`actividades-proyectos/find-all-proyecto/${id}`),
          apiFetch(`empleados-proyectos/find-all-proyecto/${id}`),
        ]);
        if (resProyecto.ok) setProyecto(await resProyecto.json());
        if (resActividades.ok) setActividades(await resActividades.json());
        if (resEquipo.ok) setEquipo(await resEquipo.json());
      } catch (error) {
        console.error("Error fetching project details:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchAllData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-brand-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!proyecto) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500">No se encontro el proyecto.</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  const columnsActividades: Column<Actividad>[] = [
    { label: "Nombre", key: "nombre" },
    {
      label: "Inicio",
      key: "fecha_inicio",
      render: (val: any) => String(val).split("T")[0],
    },
    {
      label: "Fin Estimado",
      key: "fecha_fin_estimada",
      render: (val: any) => String(val).split("T")[0],
    },
    {
      label: "Estado",
      key: "estado_actividad",
      render: (val: any) => (
        <Badge color="info" variant="light">
          {val === 1 ? "Pendiente" : val === 2 ? "En Progreso" : "Completada"}
        </Badge>
      ),
    },
    {
      label: "Prioridad",
      key: "prioridad",
      render: (val: any) => (
        <Badge
          color={val === 1 ? "error" : val === 2 ? "warning" : "success"}
          variant="light"
        >
          {val === 1 ? "Alta" : val === 2 ? "Media" : "Baja"}
        </Badge>
      ),
    },
  ];

  const columnsEquipo: Column<EmpleadoProyecto>[] = [
    {
      label: "Colaborador",
      key: "empleado",
      render: (emp: any) => `${emp.nombres} ${emp.apellidos}`,
    },
    { label: "Equipo", key: "equipo_proyecto", render: (eq: any) => eq.nombre },
    { label: "Rol", key: "rol_proyecto", render: (rol: any) => rol.nombre },
    { label: "Tarea", key: "tarea" },
  ];

  const tabs = [
    {
      value: "info",
      label: "Informacion General",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
                <Briefcase className="size-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre del Proyecto
                </p>
                <p className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  {proyecto.nombre}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-50 dark:bg-purple-500/10 rounded-lg">
                <DollarSign className="size-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Presupuesto
                </p>
                <p className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                  }).format(proyecto.presupuesto)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-50 dark:bg-amber-500/10 rounded-lg">
                <Calendar className="size-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fechas del Proyecto
                </p>
                <div className="text-sm text-gray-800 dark:text-white/90">
                  <p>
                    <span className="font-medium">Inicio:</span>{" "}
                    {proyecto.fecha_inicio.split("T")[0]}
                  </p>
                  <p>
                    <span className="font-medium">Entrega Estimada:</span>{" "}
                    {proyecto.fecha_entrega_estimada.split("T")[0]}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Descripcion
              </p>
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {proyecto.descripcion}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  Estado
                </p>
                <Badge color="success" variant="solid">
                  Activo
                </Badge>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      value: "actividades",
      label: "Actividades",
      content: (
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 flex items-center gap-2">
              <ListTodo className="size-5 text-brand-500" />
              Lista de Actividades
            </h4>
            <Badge color="info">{actividades.length} Actividades</Badge>
          </div>
          {actividades.length > 0 ? (
            <ResponsiveTable columns={columnsActividades} data={actividades} />
          ) : (
            <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/20 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-gray-500">No hay actividades registradas.</p>
            </div>
          )}
        </div>
      ),
    },
    {
      value: "equipo",
      label: "Equipo de Trabajo",
      content: (
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 flex items-center gap-2">
              <Users className="size-5 text-brand-500" />
              Personal Asignado
            </h4>
            <Badge color="info">{equipo.length} Miembros</Badge>
          </div>
          {equipo.length > 0 ? (
            <ResponsiveTable columns={columnsEquipo} data={equipo} />
          ) : (
            <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/20 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
              <p className="text-gray-500">No hay personal asignado.</p>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageBreadcrumb pageTitle="Detalle del Proyecto" />
      <div className="mb-6 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => navigate("/gestion_proyectos/proyectos")}
          startIcon={<ArrowLeft className="size-4" />}
        >
          Volver al Listado
        </Button>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-2 dark:border-gray-800 dark:bg-white/[0.03]">
        <Tabs tabs={tabs} />
      </div>
    </div>
  );
}
